import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useDeleteResource, useUpdateResourceWithoutBody } from '@app/src/api/updateHooks'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useMenuState } from '@app/src/hooks/mui-hooks'
import type { AutomationCardItem } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/AutomationsConfigLogic'
import { DeleteOutlined, EditOutlined, PauseCircleOutlined, PlayArrowOutlined } from '@mui/icons-material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { ListItemIcon, Menu, MenuItem } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'
import { useIntl } from 'react-intl'

interface AutomationMenuProps {
  automation: AutomationCardItem
  editAutomation?: () => void
  onDeleted?: () => void
  isActive?: boolean
}

const AutomationMenu: React.FC<AutomationMenuProps> = ({ automation, editAutomation, onDeleted, isActive = true }) => {
  const [isMenuOpen, menuAnchorElement, openMenu, closeMenu] = useMenuState()
  const canToggleActiveState = automation.type !== 'expectation'

  const { formatMessage } = useIntl()
  const { mutateAsync: deleteAutomationAsync, isPending: isLoadingDelete } = useDeleteResource()
  const { mutateAsync: deactivateAutomationAsync, isPending: isLoadingDeactivate } = useUpdateResourceWithoutBody({})
  const { showErrorNotification } = useErrorNotification()
  const queryClient = useQueryClient()
  const confirm = useConfirm()

  const performDelete = async () => {
    const deleteUrl =
      automation.type === 'expectation'
        ? endpoints.deleteExpectationAutomation(automation.id)
        : endpoints.deleteAutomation(automation.id)

    await deleteAutomationAsync(
      { url: deleteUrl },
      {
        onSuccess: () => {
          onDeleted?.()
          closeMenu()
        },
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  const confirmDelete = async () => {
    confirm({
      content: formatMessage({ id: 'automatedSendout.deleteConfirmButton.content' }),
      confirmationText: formatMessage({ id: 'automatedSendout.deleteConfirmButton.confirmationButtonText' }),
    })
      .then(performDelete)
      .catch(() => {
        closeMenu()
      })
  }

  const handleSetToInactive = async () => {
    await deactivateAutomationAsync(
      { url: endpoints.deactivateAutomation(automation.id) },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.Automation] })
          closeMenu()
        },
        onError: error => {
          showErrorNotification({ requestError: error })
          closeMenu()
        },
      },
    )
  }

  return (
    <>
      <IconButton onClick={openMenu}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={menuAnchorElement} open={isMenuOpen} onClose={closeMenu}>
        <MenuItem onClick={editAutomation}>
          <ListItemIcon>
            <EditOutlined />
          </ListItemIcon>
          {formatMessage({ id: 'general.edit' })}
        </MenuItem>
        {canToggleActiveState &&
          (isActive ? (
            <MenuItem onClick={handleSetToInactive} disabled={isLoadingDeactivate || isLoadingDelete}>
              <ListItemIcon>
                {isLoadingDeactivate ? <CircularProgress size={20} /> : <PauseCircleOutlined />}
              </ListItemIcon>
              {formatMessage({ id: 'automatedSendout.setToInactive' })}
            </MenuItem>
          ) : (
            <MenuItem onClick={editAutomation}>
              <ListItemIcon>
                <PlayArrowOutlined />
              </ListItemIcon>
              {formatMessage({ id: 'automatedSendout.setToActive' })}
            </MenuItem>
          ))}
        <MenuItem onClick={confirmDelete} disabled={isLoadingDelete}>
          <ListItemIcon>{isLoadingDelete ? <CircularProgress size={20} /> : <DeleteOutlined />}</ListItemIcon>
          {formatMessage({ id: 'general.delete' })}
        </MenuItem>
      </Menu>
    </>
  )
}

export default AutomationMenu
